import { createFileRoute } from "@tanstack/react-router";
import React from "react";
import { Card, CardContent, CardHeader } from "@simurgh/feathers/ui/card";
import { Divider } from "@simurgh/feathers/ui/divider";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from "@simurgh/feathers/ui/select";
import { Chip } from "@simurgh/feathers/ui/chip";
import { OnlineStatusIndicator } from "@simurgh/feathers/ui/online-status-indicator";
import { Button } from "@simurgh/feathers/ui/button";
import { Switch } from "@simurgh/feathers/ui/switch";
import { Icon } from ":shared/ui/icon";
import {
	useDictionarySource,
	usePwaStatus,
} from ":features/settings/lib/hooks";
import {
	getUserLanguage,
	setUserLanguage,
	getTranslationLanguage,
	setTranslationLanguage,
	type DictionarySource,
} from ":features/settings/model/constants";
import { DataManagement } from ":features/data-export-import";
import {
	usePronunciationStore,
	usePronunciationActions,
	PRONUNCIATION_PRESETS,
	type PresetName,
} from ":shared/stores/pronunciation-store";
import { useLocalPronunciationStore } from ":shared/stores/local-pronunciation-store";
import { useDebugMode } from ":shared/hooks/use-debug-mode";
import { useRouter } from "@tanstack/react-router";
import { log } from ":shared/lib/logger";
import { Description, Label } from "@simurgh/feathers/ui/field";
import { useTranslation } from "react-i18next";

const version =
	(typeof import.meta.env.VITE_VERSION !== "undefined" &&
		import.meta.env.VITE_VERSION) ||
	"unknown";

export const Route = createFileRoute("/$locale/dashboard/settings")({
	component: SettingsPage,
});

function SettingsPage() {
	const { t } = useTranslation();
	const { locale } = Route.useParams();
	const { source, setSource } = useDictionarySource();
	const { displayMode, isPwa } = usePwaStatus();
	const { isDebugEnabled, isClient, toggleDebugMode } = useDebugMode();
	const router = useRouter();

	const [userLang, setUserLang] = React.useState<string>(() =>
		getUserLanguage(),
	);
	const [translationLang, setTranslationLang] = React.useState<string>(() =>
		getTranslationLanguage(),
	);

	// Pronunciation settings
	const pronunciationSettings = usePronunciationStore();
	const pronunciationActions = usePronunciationActions();
	const localStore = useLocalPronunciationStore();
	const [showPronunciationAdvanced, setShowPronunciationAdvanced] =
		React.useState(false);

	const DICTIONARY_OPTIONS: Array<{ value: DictionarySource; label: string }> =
		[
			{ value: "dwds", label: t("pages.settings.dictionary.dwdsDefault") },
			{ value: "wiktionary", label: t("dictionary.wiktionary") },
		];

	const USER_LANG_OPTIONS = [
		{ value: "en", label: t("languages.en") },
		{ value: "de", label: t("languages.de") },
	];

	const TRANSLATION_LANG_OPTIONS = [
		{ value: "en", label: t("languages.en") },
		{ value: "de", label: t("languages.de") },
		{ value: "es", label: t("languages.es") },
		{ value: "fr", label: t("languages.fr") },
		{ value: "it", label: t("languages.it") },
		{ value: "pt", label: t("languages.pt") },
		{ value: "fa", label: t("languages.fa") },
	];

	const handleDictionaryChange = (key: React.Key | null) => {
		if (!key) {
			return;
		}
		const v = key as DictionarySource | undefined;
		if (v === "dwds" || v === "wiktionary") {
			setSource(v);
		}
	};

	const handleUserLangChange = (key: React.Key | null) => {
		if (!key) {
			return;
		}
		const v = String(key ?? "");
		setUserLang(v);
		setUserLanguage(v);
	};

	const handleTranslationLangChange = (key: React.Key | null) => {
		if (!key) {
			return;
		}
		const v = String(key ?? "");
		setTranslationLang(v);
		setTranslationLanguage(v);
	};

	return (
		<>
			<title>
				{t("pages.settings.title")} - {t("app.name")}
			</title>
			<meta name="description" content={t("pages.settings.description")} />
			<div
				className="w-full h-full overflow-y-auto"
				style={{
					paddingBottom: "calc(1rem + env(safe-area-inset-bottom))",
				}}
			>
				<div className="px-4 md:px-6 py-6 md:py-8 space-y-6 md:space-y-8">
					<header className="space-y-1">
						<h1 className="text-2xl md:text-3xl font-bold tracking-tight">
							{t("pages.settings.title")}
						</h1>
						<p className="text-foreground/70">
							{t("pages.settings.description")}
						</p>
					</header>

					<section aria-labelledby="app-info-heading">
						<Card>
							<CardHeader>
								<div className="flex flex-col gap-1">
									<h2 id="app-info-heading" className="text-lg font-semibold">
										{t("pages.settings.appInformation.title")}
									</h2>
									<p className="text-foreground/70 text-sm">
										{t("pages.settings.appInformation.description")}
									</p>
								</div>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div className="flex items-center justify-between rounded-lg border p-3">
										<span className="text-sm font-medium">
											{t("pages.settings.version")}
										</span>
										<Chip
											size="sm"
											intent="primary"
											color="default"
											className="font-mono"
										>
											{version || t("common.unknown")}
										</Chip>
									</div>

									<div className="flex items-center justify-between rounded-lg border p-3">
										<span className="text-sm font-medium">
											{t("pages.settings.connectivity")}
										</span>
										<div className="flex items-center gap-2">
											<OnlineStatusIndicator />
											<span className="sr-only" aria-live="polite">
												{t("pages.settings.connectivityStatusUpdated")}
											</span>
										</div>
									</div>

									<div className="flex items-center justify-between rounded-lg border p-3">
										<span className="text-sm font-medium">
											{t("pages.settings.pwa")}
										</span>
										<Chip
											size="sm"
											intent="primary"
											color={
												isPwa
													? "success"
													: displayMode === "unknown"
														? "default"
														: "warning"
											}
										>
											{displayMode === "unknown"
												? t("common.unknown")
												: isPwa
													? t("common.yes")
													: t("common.no")}
										</Chip>
									</div>
								</div>
							</CardContent>
						</Card>
					</section>

					<section aria-labelledby="dictionary-heading">
						<Card>
							<CardHeader>
								<div className="flex flex-col gap-1">
									<h2 id="dictionary-heading" className="text-lg font-semibold">
										{t("pages.settings.dictionary.title")}
									</h2>
									<p className="text-foreground/70 text-sm">
										{t("pages.settings.dictionary.description")}
									</p>
								</div>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div className="flex flex-col gap-2">
										<Select
											id="dict-source"
											aria-label={t("pages.settings.dictionary.source")}
											value={source}
											onChange={handleDictionaryChange}
											className="max-w-xs"
										>
											<Label>{t("pages.settings.dictionary.source")}</Label>
											<SelectTrigger />
											<SelectContent items={DICTIONARY_OPTIONS}>
												{(opt) => (
													<SelectItem id={opt.value} textValue={opt.label}>
														{opt.label}
													</SelectItem>
												)}
											</SelectContent>
											<Description>
												{t("pages.settings.dictionary.sourceDescription")}
											</Description>
										</Select>
									</div>
								</div>
							</CardContent>
						</Card>
					</section>

					<section aria-labelledby="language-heading">
						<Card>
							<CardHeader>
								<div className="flex items-center justify-between gap-2 w-full">
									<div className="flex flex-col gap-1">
										<h2 id="language-heading" className="text-lg font-semibold">
											{t("pages.settings.language.title")}
										</h2>
										<p className="text-foreground/70 text-sm">
											{t("pages.settings.language.description")}
										</p>
									</div>
									<Chip size="sm" intent="warning">
										{t("pages.settings.language.experimental")}
									</Chip>
								</div>
							</CardHeader>
							<CardContent>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div className="flex flex-col gap-2">
										<Select
											id="user-lang"
											aria-label={t("pages.settings.language.userLanguage")}
											value={userLang}
											onChange={handleUserLangChange}
											className="max-w-xs"
										>
											<Label>{t("pages.settings.language.userLanguage")}</Label>
											<SelectTrigger />
											<SelectContent items={USER_LANG_OPTIONS}>
												{(opt) => (
													<SelectItem id={opt.value} textValue={opt.label}>
														{opt.label}
													</SelectItem>
												)}
											</SelectContent>
											<Description className="text-xs text-foreground/70">
												{t("pages.settings.language.userLanguageDescription")}
											</Description>
										</Select>
									</div>
								</div>
							</CardContent>
						</Card>
					</section>

					<section aria-labelledby="translation-language-heading">
						<Card>
							<CardHeader>
								<div className="flex flex-col gap-1">
									<h2
										id="translation-language-heading"
										className="text-lg font-semibold"
									>
										{t("pages.settings.translationLanguage.title")}
									</h2>
									<p className="text-foreground/70 text-sm">
										{t("pages.settings.translationLanguage.description")}
									</p>
								</div>
							</CardHeader>
							<CardContent>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div className="flex flex-col gap-2">
										<Select
											id="translation-lang"
											aria-label={t("pages.settings.translationLanguage.title")}
											value={translationLang}
											onChange={handleTranslationLangChange}
											className="max-w-xs"
										>
											<Label
												htmlFor="translation-language"
												className="text-sm font-medium"
											>
												{t("pages.settings.translationLanguage.targetLanguage")}
											</Label>

											<SelectTrigger />
											<SelectContent items={TRANSLATION_LANG_OPTIONS}>
												{(opt) => (
													<SelectItem id={opt.value} textValue={opt.label}>
														{opt.label}
													</SelectItem>
												)}
											</SelectContent>
											<Description className="text-xs text-foreground/70">
												{t(
													"pages.settings.translationLanguage.targetLanguageDescription",
												)}
											</Description>
										</Select>
									</div>
								</div>
							</CardContent>
						</Card>
					</section>

					<section aria-labelledby="pronunciation-heading">
						<Card>
							<CardHeader>
								<div className="flex flex-row items-center justify-between">
									<div className="flex flex-col gap-1">
										<h2
											id="pronunciation-heading"
											className="text-lg font-semibold"
										>
											{t("pronunciation.settings")}
										</h2>
										<p className="text-foreground/70 text-sm">
											{t("pronunciation.settingsDescription")}
										</p>
									</div>
									<Button
										size="sm"
										intent="secondary"
										onPress={() =>
											setShowPronunciationAdvanced(!showPronunciationAdvanced)
										}
									>
										<Icon name="hugeicons-settings-02" size={16} />
										{showPronunciationAdvanced
											? t("pronunciation.simple")
											: t("pronunciation.advanced")}
									</Button>
								</div>
							</CardHeader>
							<CardContent className="space-y-6">
								{/* Enable/Disable Toggle */}
								<div className="flex items-center justify-between p-4 rounded-lg border">
									<div className="flex flex-col gap-1">
										<span className="text-sm font-medium">
											{t("pronunciation.enable")}
										</span>
										<span className="text-xs text-foreground/60">
											{t("pronunciation.enableDescription")}
										</span>
									</div>
									<Switch
										isSelected={pronunciationSettings.enabled}
										onChange={pronunciationActions.toggleEnabled}
									/>
								</div>

								{pronunciationSettings.enabled && (
									<>
										{/* Quick Presets */}
										<div className="space-y-3">
											<div className="flex items-center gap-2">
												<Icon name="hugeicons-magic-wand-01" size={20} />
												<span className="font-medium">
													{t("pronunciation.quickPresets")}
												</span>
											</div>
											<div className="flex flex-wrap gap-2">
												{Object.keys(PRONUNCIATION_PRESETS).map((preset) => (
													<Button
														key={preset}
														size="sm"
														intent="outline"
														onPress={() =>
															pronunciationActions.applyPreset(
																preset as PresetName,
															)
														}
														className="capitalize"
													>
														{preset}
													</Button>
												))}
											</div>
										</div>

										{/* Basic Settings */}
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											{/* Global Repeat Count */}
											<div className="flex items-center justify-between p-3 rounded-lg border">
												<div className="flex flex-col gap-1">
													<span className="text-sm font-medium">
														{t("pronunciation.defaultRepeatCount")}
													</span>
													<span className="text-xs text-foreground/60">
														{t("pronunciation.delayBetweenRepeatsDescription")}
													</span>
												</div>
												<div className="flex items-center gap-2">
													<Button
														size="sq-sm"
														intent="plain"
														onPress={() =>
															pronunciationActions.setGlobalRepeatCount(
																Math.max(
																	1,
																	pronunciationSettings.globalRepeatCount - 1,
																),
															)
														}
													>
														<Icon name="hugeicons-minus-sign" size={14} />
													</Button>
													<Chip
														size="sm"
														intent="primary"
														color="primary"
														className="min-w-8 text-center"
													>
														{pronunciationSettings.globalRepeatCount}
													</Chip>
													<Button
														size="sq-sm"
														intent="plain"
														onPress={() =>
															pronunciationActions.setGlobalRepeatCount(
																Math.min(
																	10,
																	pronunciationSettings.globalRepeatCount + 1,
																),
															)
														}
													>
														<Icon name="hugeicons-plus-sign" size={14} />
													</Button>
												</div>
											</div>

											{/* Auto-play */}
											<div className="flex items-center justify-between p-3 rounded-lg border">
												<div className="flex flex-col gap-1">
													<span className="text-sm font-medium">
														{t("pronunciation.autoPlayOnCards")}
													</span>
													<span className="text-xs text-foreground/60">
														{t("pronunciation.autoPlayDescription")}
													</span>
												</div>
												<Switch
													isSelected={pronunciationSettings.autoPlay}
													onChange={pronunciationActions.toggleAutoPlay}
												/>
											</div>
										</div>

										{/* Advanced Settings */}
										{showPronunciationAdvanced && (
											<div className="space-y-4 border-t pt-4">
												<div className="flex items-center gap-2 mb-4">
													<Icon name="hugeicons-settings-03" size={20} />
													<span className="font-medium text-foreground/80">
														{t("pronunciation.advancedSettings")}
													</span>
												</div>

												<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
													{/* Repeat Delay */}
													<div className="space-y-2">
														<div className="flex items-center justify-between">
															<span className="text-sm font-medium">
																{t("pronunciation.delayBetweenRepeats")}
															</span>
															<Chip size="sm" intent="secondary">
																{pronunciationSettings.repeatDelay}ms
															</Chip>
														</div>
														<input
															type="range"
															min={200}
															max={3000}
															step={100}
															value={pronunciationSettings.repeatDelay}
															onChange={(e) =>
																pronunciationActions.setRepeatDelay(
																	parseInt(e.target.value),
																)
															}
															className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer"
														/>
														<div className="flex justify-between text-xs text-foreground/60">
															<span>{t("pronunciation.fast")} (200ms)</span>
															<span>{t("pronunciation.slow")} (3s)</span>
														</div>
													</div>

													{/* Playback Speed */}
													<div className="space-y-2">
														<div className="flex items-center justify-between">
															<span className="text-sm font-medium">
																{t("pronunciation.playbackSpeed")}
															</span>
															<Chip size="sm" intent="warning">
																{pronunciationSettings.playbackRate}x
															</Chip>
														</div>
														<input
															type="range"
															min={0.25}
															max={3.0}
															step={0.05}
															value={pronunciationSettings.playbackRate}
															onChange={(e) =>
																pronunciationActions.setPlaybackRate(
																	parseFloat(e.target.value),
																)
															}
															className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer"
														/>
														<div className="flex justify-between text-xs text-foreground/60">
															<span>{t("pronunciation.slow")} (0.25x)</span>
															<span>{t("pronunciation.normal")} (1x)</span>
															<span>{t("pronunciation.fast")} (3x)</span>
														</div>
													</div>

													{/* Volume */}
													<div className="space-y-2">
														<div className="flex items-center justify-between">
															<span className="text-sm font-medium">
																{t("pronunciation.volume")}
															</span>
															<Chip size="sm" intent="success">
																{Math.round(pronunciationSettings.volume * 100)}
																%
															</Chip>
														</div>
														<input
															type="range"
															min={0}
															max={1}
															step={0.05}
															value={pronunciationSettings.volume}
															onChange={(e) =>
																pronunciationActions.setVolume(
																	parseFloat(e.target.value),
																)
															}
															className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer"
														/>
														<div className="flex justify-between text-xs text-foreground/60">
															<span>{t("pronunciation.mute")}</span>
															<span>100%</span>
														</div>
													</div>

													{/* UI Preferences */}
													<div className="space-y-3">
														<div className="flex items-center justify-between">
															<span className="text-sm font-medium">
																{t("pronunciation.showControls")}
															</span>
															<Switch
																isSelected={pronunciationSettings.showControls}
																onChange={
																	pronunciationActions.toggleShowControls
																}
															/>
														</div>
														<div className="flex items-center justify-between">
															<span className="text-sm font-medium">
																{t("pronunciation.useAnimations")}
															</span>
															<Switch
																isSelected={pronunciationSettings.useAnimations}
																onChange={pronunciationActions.toggleAnimations}
															/>
														</div>
													</div>
												</div>
											</div>
										)}

										{/* Local Overrides Statistics */}
										<div className="bg-content2 rounded-lg p-4">
											<div className="flex items-center justify-between mb-2">
												<span className="text-sm font-medium">
													{t("pronunciation.localOverrides")}
												</span>
												<Chip size="sm" intent="primary">
													{Object.keys(localStore.localSettings).length}{" "}
													{t("pronunciation.items")}
												</Chip>
											</div>
											<p className="text-xs text-foreground/60 mb-3">
												{t("pronunciation.localOverridesDescription")}
											</p>
											<div className="flex gap-2">
												<Button
													size="sm"
													intent="danger"
													onPress={() => {
														if (
															confirm(
																t("pronunciation.clearAllOverridesConfirm"),
															)
														) {
															localStore.clearAllLocalSettings();
														}
													}}
													isDisabled={
														Object.keys(localStore.localSettings).length === 0
													}
												>
													{t("pronunciation.clearAllOverrides")}
												</Button>
												<Button
													size="sm"
													intent="outline"
													onPress={() => localStore.cleanupOldSettings(30)}
													isDisabled={
														Object.keys(localStore.localSettings).length === 0
													}
												>
													{t("pronunciation.cleanupOld")}
												</Button>
											</div>
										</div>

										{/* Reset Section */}
										<div className="pt-4 border-t">
											<Button
												intent="danger"
												size="sm"
												onPress={() => {
													if (
														confirm(t("pronunciation.resetAllSettingsConfirm"))
													) {
														pronunciationActions.resetSettings();
													}
												}}
												className="w-full"
											>
												<Icon name="hugeicons-refresh" size={16} />
												{t("pronunciation.resetAllSettings")}
											</Button>
										</div>
									</>
								)}
							</CardContent>
						</Card>
					</section>

					<section aria-labelledby="data-management-heading">
						<div className="space-y-2 mb-4">
							<h2
								id="data-management-heading"
								className="text-lg font-semibold"
							>
								{t("pages.settings.dataManagement.title")}
							</h2>
							<p className="text-foreground/70 text-sm">
								{t("pages.settings.dataManagement.description")}
							</p>
						</div>
						<DataManagement />
					</section>

					<section aria-labelledby="developer-heading">
						<Card>
							<CardHeader>
								<div className="flex flex-col gap-1">
									<h2 id="developer-heading" className="text-lg font-semibold">
										{t("pages.settings.developer.title")}
									</h2>
									<p className="text-foreground/70 text-sm">
										{t("pages.settings.developer.description")}
									</p>
								</div>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="flex items-center justify-between rounded-lg border p-3">
									<div className="flex flex-col gap-1">
										<span className="text-sm font-medium">
											{t("pages.settings.debugLogs")}
										</span>
										<span className="text-xs text-foreground/60">
											{t("pages.settings.developer.debugLogsEnable")}
										</span>
									</div>
									<Switch
										isSelected={isDebugEnabled}
										onChange={() => {
											toggleDebugMode();
											log.info(
												`Debug mode ${!isDebugEnabled ? "enabled" : "disabled"}`,
												undefined,
												"settings",
											);
										}}
										isDisabled={!isClient}
									>
										{isDebugEnabled
											? t("pages.settings.developer.enabled")
											: t("pages.settings.developer.disabled")}
									</Switch>
								</div>

								{isDebugEnabled && (
									<div className="flex flex-col gap-2">
										<Button
											onPress={() => {
												log.info(
													"Navigating to debug logs viewer",
													undefined,
													"settings",
												);
												router.navigate({
													to: "/$locale/dashboard/debug-logs",
													params: { locale },
												});
											}}
											className="w-full"
											intent="primary"
										>
											<Icon name="hugeicons-file-view" size={16} />
											{t("pages.settings.developer.viewDebugLogs")}
										</Button>
										<p className="text-xs text-foreground/60">
											{t("pages.settings.developer.debugLogsDescription")}
										</p>
									</div>
								)}
							</CardContent>
						</Card>
					</section>

					<footer>
						<Divider />
						<p className="text-xs text-foreground/60 mt-3">
							{t("pages.settings.footer")}
						</p>
					</footer>
				</div>
			</div>
		</>
	);
}
